import React from 'react';
import { InputAdornment } from '@mui/material';
import { FormikProps, isNaN } from 'formik';
import { useTranslation } from 'react-i18next';
import { TextField } from '../../../../form/textfield/textfield';
import { Grid } from '../../../../grid/grid';
import { useRootStore } from '../../../../../../providers/root-store-provider';
import { FairPriceFormValues } from '../../../form-values/fair-price-form-values-interface';
import { cn } from '../../../../../../utils/tailwind';

interface FairPriceTabPeParametersProps {
    formikProps: FormikProps<FairPriceFormValues>;
    showResult: boolean;
}

export const FairPriceTabPeParameters: React.FC<FairPriceTabPeParametersProps> = ({ formikProps, showResult }) => {
    const { t } = useTranslation();
    const {
        symbolsStore: { symbolFairPriceStore },
    } = useRootStore();
    const { values, setFieldValue, errors } = formikProps;
    const showError = (field: keyof FairPriceFormValues) => !!errors[field] && !!values[field];

    return (
        <div
            className={cn(
                'rounded-medium border border-border px-small pt-medium',
                showResult ? 'mb-[-3rem] pb-[calc(theme(spacing.small)+theme(spacing.medium))]' : 'mb-vsmall pb-small',
            )}
        >
            <div className="mb-medium text-h3 font-bold leading-h3 text-brand-secondary">
                {t('layout.dialogs.fairPrice.pe.params')}
            </div>
            <Grid cols={{ zero: 1, sm: 3 }} gap={2} marginBottom={'small'}>
                <TextField
                    label={t('layout.dialogs.fairPrice.pe.eps')}
                    value={values.eps}
                    fullWidth
                    error={showError('eps')}
                    helperText={showError('eps') ? errors.eps : undefined}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <span className="text-text-muted">$</span>
                            </InputAdornment>
                        ),
                    }}
                    onChange={(e) => {
                        const raw = e.target.value;
                        setFieldValue('eps', raw);
                        const val = Number(raw.replace(',', '.'));
                        if (!isNaN(val)) symbolFairPriceStore.setInputEps(val);
                    }}
                />
                <TextField
                    label={t('layout.dialogs.fairPrice.pe.epsGrowthRate')}
                    value={values.growthRate ?? ''}
                    fullWidth
                    error={showError('growthRate')}
                    helperText={showError('growthRate') ? errors.growthRate : undefined}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <span className="text-text-muted">%</span>
                            </InputAdornment>
                        ),
                    }}
                    onChange={(e) => {
                        const raw = e.target.value;
                        setFieldValue('growthRate', raw);
                        const val = Number(raw.replace(',', '.'));
                        if (!isNaN(val)) symbolFairPriceStore.setInputGrowthRate(val);
                    }}
                />
                <TextField
                    label={t('layout.dialogs.fairPrice.pe.peMultiple')}
                    value={values.peMultiple}
                    fullWidth
                    error={showError('peMultiple')}
                    helperText={showError('peMultiple') ? errors.peMultiple : undefined}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <span className="text-text-muted">×</span>
                            </InputAdornment>
                        ),
                    }}
                    onChange={(e) => {
                        const raw = e.target.value;
                        setFieldValue('peMultiple', raw);
                        const val = Number(raw.replace(',', '.'));
                        if (!isNaN(val) && val > 0) symbolFairPriceStore.setInputPeMultiple(val);
                    }}
                />
            </Grid>
            <TextField
                label={t('layout.dialogs.fairPrice.result.requiredReturn')}
                value={values.peRequiredReturn}
                fullWidth
                error={showError('peRequiredReturn')}
                helperText={showError('peRequiredReturn') ? errors.peRequiredReturn : undefined}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <span className="text-text-muted">%</span>
                        </InputAdornment>
                    ),
                }}
                onChange={(e) => {
                    const raw = e.target.value;
                    setFieldValue('peRequiredReturn', raw);
                    const val = Number(raw.replace(',', '.'));
                    if (!isNaN(val) && val > 0) symbolFairPriceStore.setInputPeRequiredReturn(val);
                }}
            />
        </div>
    );
};
